import dayjs from "dayjs";
import { REPORT_GAIN_TYPE_COLUMN_LABEL } from "../../../utils/reports/reportDisplayLabels";

export type FieldColumn = {
  key: string;
  value: string;
  locked?: boolean;
  minWidth?: number;
  align?: "left" | "center" | "right";
};

export const PMS_TABLE_SEGMENT = "PMS";

export const PMS_CONSOLIDATED_REPORT = "Consolidated Report";
export const PMS_CAP_REPORT = "Cap report";
export const PMS_SECTOR_REPORT = "Sector report";

export const PMS_REPORT_TYPE_OPTIONS = [
  { value: "Holding", label: "Unrealized" },
  { value: "Realized", label: "Realized" },
  { value: "All", label: "All" },
  { value: "Transaction", label: "Transaction" },
  { value: PMS_CAP_REPORT, label: "Market Cap Report" },
  { value: PMS_SECTOR_REPORT, label: "Sector Report" },
  { value: PMS_CONSOLIDATED_REPORT, label: PMS_CONSOLIDATED_REPORT },
] as const;

export const PMS_QUICK_REPORTS = [
  { id: "holding", label: "Unrealized", reportType: "Holding" },
  { id: "realized", label: "Realized", reportType: "Realized" },
  { id: "all", label: "All", reportType: "All" },
  { id: "transaction", label: "Transaction", reportType: "Transaction" },
  { id: "cap", label: "Market Cap", reportType: PMS_CAP_REPORT },
  { id: "sector", label: "Sector", reportType: PMS_SECTOR_REPORT },
  { id: "consolidated", label: "Consolidated", reportType: PMS_CONSOLIDATED_REPORT },
];

export const INVESTOR_COLUMN: FieldColumn = {
  key: "investorDisplay",
  value: "Investor Name",
  locked: true,
  minWidth: 200,
};

/** aumsampaan PMS Holding preset (modern report panel) */
export const PMS_UNREALIZED_HOLDING_COLUMNS: FieldColumn[] = [
  { key: "schemeName", value: "Scheme Name", locked: true, minWidth: 200 },
  { key: "asOfDate", value: "As of Date", minWidth: 112 },
  { key: "currentDate", value: "Current Date", minWidth: 112 },
  { key: "investedAmount", value: "Invested Amt.", minWidth: 128 },
  { key: "currentAmount", value: "Current Amt.", minWidth: 128 },
  { key: "gainLoss", value: "P&L", minWidth: 132 },
  { key: "gainLossPercentage", value: "P&L %", minWidth: 96 },
];

export const PMS_REALIZED_COLUMNS: FieldColumn[] = [
  { key: "schemeName", value: "Scheme Name", locked: true, minWidth: 200 },
  { key: "type", value: REPORT_GAIN_TYPE_COLUMN_LABEL, locked: true, minWidth: 132 },
  { key: "currentDate", value: "Current Date", minWidth: 112 },
  { key: "transactionDate", value: "Date", minWidth: 112 },
  { key: "purchaseNAV", value: "Sell Price", minWidth: 112 },
  { key: "avgBuyPrice", value: "Avg Buy Price", minWidth: 112 },
  { key: "saleValue", value: "Sale Value", minWidth: 128 },
  { key: "realisedPnl", value: "Realized P&L", minWidth: 132 },
  { key: "realisedPNLPercentage", value: "Realized P&L%", minWidth: 120 },
];

export const PMS_TRANSACTION_COLUMNS: FieldColumn[] = [
  { key: "transType", value: "Type", locked: true, minWidth: 120 },
  { key: "schemeName", value: "Scheme Name", locked: true, minWidth: 220 },
  { key: "folioNumber", value: "Acc No", minWidth: 120 },
  { key: "transactionDate", value: "Date", minWidth: 112 },
  { key: "asOfDate", value: "As of Date", minWidth: 112 },
  { key: "amount", value: "Amount", minWidth: 128 },
  { key: "currentAmount", value: "Current Value", minWidth: 128 },
  { key: "unrealisedPnL", value: "Unrealised P&L", minWidth: 132 },
  { key: "unrealisedPnLPercentage", value: "Unrealised P&L %", minWidth: 120 },
  { key: "realisedPnL", value: "Realised P&L", minWidth: 132 },
  { key: "realisedPNLPercentage", value: "Realised P&L %", minWidth: 120 },
  { key: "days", value: "Avg. Days", minWidth: 88 },
];

export const PMS_ALL_COMMON_COLUMNS: FieldColumn[] = [
  { key: "schemeName", value: "Scheme Name", locked: true, minWidth: 200 },
  { key: "type", value: REPORT_GAIN_TYPE_COLUMN_LABEL, locked: true, minWidth: 132 },
  { key: "folioNumber", value: "Folio Number", minWidth: 120 },
  { key: "purchaseDate", value: "Purchase Date", minWidth: 112 },
  { key: "currentDate", value: "Current Date", minWidth: 112 },
  { key: "costValue", value: "Invested Amount", minWidth: 128 },
  { key: "currentValue", value: "Current Value", minWidth: 128 },
  { key: "gainLoss", value: "P&L", minWidth: 132 },
  { key: "gainLossPercentage", value: "P&L %", minWidth: 96 },
];

/** aumsampaan `PMSMarketCapFieldTitles` (Investor Name instead of Member Name). */
export const PMS_MARKET_CAP_COLUMNS: FieldColumn[] = [
  { key: "schemeName", value: "Scheme Name", locked: true, minWidth: 160 },
  { key: "marketCap", value: "Market Cap", minWidth: 120 },
  { key: "isin", value: "ISIN of Stock", minWidth: 140, align: "center" },
  { key: "stockName", value: "Stock Name", minWidth: 160 },
  { key: "exposurePct", value: "Market Cap %", minWidth: 180, align: "left" },
];

/** aumsampaan `PMSSectorFieldTitles` (Investor Name instead of Member Name). */
export const PMS_SECTOR_COLUMNS: FieldColumn[] = [
  { key: "schemeName", value: "Scheme Name", locked: true, minWidth: 160 },
  { key: "isin", value: "ISIN of Stock", minWidth: 140, align: "center" },
  { key: "stockName", value: "Stock Name", minWidth: 160 },
  { key: "sector", value: "Sector", minWidth: 140 },
  { key: "percentageShare", value: "Sector %", minWidth: 180, align: "left" },
];

/** aumsampaan `buildPmsConsolidatedFieldTitles`. */
export const getPmsConsolidatedColumns = (asOnLabel?: string): FieldColumn[] => [
  { key: "name", value: "Scheme / Stock", locked: true, minWidth: 260 },
  {
    key: "closingValue",
    value: `Closing Value on ${asOnLabel ?? dayjs().format("DD-MMM-YYYY")}`,
    minWidth: 160,
    align: "right",
  },
  { key: "mtdPercent", value: "MTD %", minWidth: 96, align: "right" },
  { key: "fytdPercent", value: "FYTD %", minWidth: 96, align: "right" },
  { key: "oneYearPercent", value: "1 Year %", minWidth: 96, align: "right" },
  { key: "threeYearPercent", value: "3 Year %", minWidth: 96, align: "right" },
];

export const getColumnsForPmsReportType = (reportType: string): FieldColumn[] => {
  switch (reportType) {
    case "Holding":
      return PMS_UNREALIZED_HOLDING_COLUMNS;
    case "Realized":
      return PMS_REALIZED_COLUMNS;
    case "All":
      return PMS_ALL_COMMON_COLUMNS;
    case "Transaction":
      return PMS_TRANSACTION_COLUMNS;
    case PMS_CAP_REPORT:
      return PMS_MARKET_CAP_COLUMNS;
    case PMS_SECTOR_REPORT:
      return PMS_SECTOR_COLUMNS;
    case PMS_CONSOLIDATED_REPORT:
      return getPmsConsolidatedColumns();
    default:
      return PMS_UNREALIZED_HOLDING_COLUMNS;
  }
};

export const isPmsBreakdownReport = (reportType: string): boolean =>
  reportType === PMS_CAP_REPORT || reportType === PMS_SECTOR_REPORT;

export const isPmsConsolidatedReport = (reportType: string): boolean =>
  reportType === PMS_CONSOLIDATED_REPORT;

export const reportTypeNeedsDateRange = (reportType: string): boolean =>
  reportType === "Holding";
